import React, { useMemo } from 'react'
import { BG, BORDER, TEXT, ACCENT, FONT, TABULAR, font } from '@/Components/Shell/tokens'
import { readableTeamColor } from '@/utils/colors'
import GameFeedComposer, { relativeTime, type TimelineEntry } from './GameFeedComposer'

/**
 * The Bleachers rail — the fan conversation, which the modal had nowhere to put.
 *
 * Two kinds of voice share one row shell: fans, and sideline cutaways. A player
 * REACTING to a play stays inline with that play — moved here it became a quote
 * with no snap attached.
 *
 * ⚠️ ACCENT IS THE SPEAKER'S TEAM (owner, 2026-08-23). It used to be the personality
 * TIER, so the colour told you how rare that player's temperament is — a fact about the
 * generator, not about the game you are watching. Two players on opposite sides could
 * read as the same voice, and the same player could change colour between games. Team
 * colour answers the question a reader of a live feed actually has: which side is this.
 * Corrected through `readableTeamColor` because it is used as TEXT on the tag, and team
 * colours are data — several are navy or maroon.
 */

export interface RailEntry {
  key: string
  kind: 'player' | 'sideline'
  text: string
  personality?: string
  speaker: string
  teamId?: number | null
  teamAbbr?: string | null
  /** The play this was said about. Cutaways fire between plays and carry none. */
  playQuote?: string | null
  when?: string | null
  /** ISO wall clock, stamped when the cutaway fired, so the rail can sort this against
   *  the fan posts instead of stacking the two halves. Absent on a cutaway from before
   *  the server stamped them. */
  createdAt?: string | null
}

/**
 * Pull the SIDELINE CUTAWAYS out of the play list.
 *
 * Cutaways fire between plays and are about nothing in particular, so the rail
 * is where they belong. Player reactions stay inline with their play.
 *
 * Newest first, matching the play list's own order — the plays arrive
 * newest-first and these are read off them without re-sorting.
 */
export function railEntriesFromPlays(plays: any[] | undefined): RailEntry[] {
  if (!plays?.length) return []
  const out: RailEntry[] = []
  plays.forEach((play, i) => {
    if (play.isSidelineCutaway && play.sidelineCutaway) {
      const cutaway = play.sidelineCutaway
      out.push({
        key: `cutaway-${i}`,
        kind: 'sideline',
        text: cutaway.text,
        personality: cutaway.personality,
        speaker: cutaway.playerName || cutaway.name || cutaway.teamAbbr || 'Sideline',
        teamId: cutaway.teamId,
        teamAbbr: cutaway.teamAbbr,
        playQuote: null,
        createdAt: cutaway.createdAt ?? null,
      })
      return
    }
    // ⚠️ Player reactions are NOT collected here. They render inline with the
    // play they are about — pulled into the rail they became a quote with no
    // snap attached, which is the opposite of what makes them worth reading.
  })
  return out
}

const Tag: React.FC<{ label: string; accent: string }> = ({ label, accent }) => (
  <span style={{
    ...font(700, 8, 1, '0.02em'),
    color: accent,
    border: `1px solid ${accent}99`,
    padding: '4px 5px', flexShrink: 0,
  }}>{label}</span>
)

/**
 * Is this line SPOKEN, or is it something the player is doing?
 *
 * ⚠️ THE POOL HOLDS BOTH, and they want opposite treatment. A sideline pool mixes
 * narration — "{name} is watching the cooler with quiet longing." — with speech —
 * "Anyone seen my water bottle?". Rendered identically, every narration line came out
 * as a quote with the player's name inside it, under a header carrying that same name
 * again. Which is exactly how it was reported.
 *
 * The name is the tell: the templates interpolate `{name}` at the front of a narration
 * line and never into a spoken one.
 */
const isNarration = (text: string, speaker: string): boolean =>
  !!speaker && text.trimStart().toLowerCase().startsWith(speaker.toLowerCase())

const Entry: React.FC<{ entry: RailEntry; teamColor?: string }> = ({ entry, teamColor }) => {
  const accent = teamColor ? readableTeamColor(teamColor, BG.card) : ACCENT.info
  const narration = isNarration(entry.text, entry.speaker)
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: '8px',
      padding: '12px 14px', borderBottom: `1px solid ${BORDER.hairline}`,
      background: `${accent}0d`,
    }}>
      {entry.playQuote && (
        <div style={{
          padding: '7px 9px', background: BG.panel,
          borderLeft: `2px solid ${accent}`,
        }}>
          <span style={{ ...font(400, 10, 1.4), color: TEXT.muted }}>{entry.playQuote}</span>
        </div>
      )}
      {/* ⚠️ THE SAME HEADER SHAPE A FAN POST HAS (owner): mark, who, then the age on the
          right. The two voices were laid out differently enough that the rail read as two
          feeds stacked. What separates them now is what they ARE — a player line carries
          the personality tint and its own tag, a fan post is plain — rather than a
          different arrangement of the same parts. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {entry.teamId != null && (
          <img
            src={`/avatars/${entry.teamId}.png`}
            alt=""
            width={18}
            height={18}
            style={{ borderRadius: '50%', flexShrink: 0, display: 'block' }}
          />
        )}
        {/* A narration line says the player's name in the sentence itself, so repeating
            it here is the duplication that was reported. The tag still says who is
            speaking in the sense that matters: this is the field, not the stands. */}
        {!narration && (
          <span style={{
            ...font(700, 11), color: TEXT.strong, minWidth: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{entry.speaker}</span>
        )}
        <Tag label={entry.kind === 'sideline' ? 'SIDELINE' : 'ON THE FIELD'} accent={accent} />
        <span style={{ flex: 1 }} />
        {entry.createdAt && (
          <span style={{ ...font(400, 10), color: TEXT.muted, ...TABULAR }}>
            {relativeTime(entry.createdAt)}
          </span>
        )}
      </div>
      <div style={{
        borderLeft: `2px solid ${accent}`, background: `${accent}17`,
        padding: '8px 10px',
      }}>
        {/* 14px, matching a fan post — these were 12 and read as the small print of a
            feed whose other half was the size above it. Narration is not italicised:
            it is the room being described, not something anybody said. */}
        <span style={{
          ...font(400, 14, 1.5), color: TEXT.body,
          ...(narration ? {} : { fontStyle: 'italic' }),
        }}>
          {entry.text}
        </span>
      </div>
    </div>
  )
}

const LiveDot: React.FC = () => (
  <span className="pulse" style={{
    width: '5px', height: '5px', borderRadius: '50%',
    background: ACCENT.live, flexShrink: 0, display: 'block',
  }} />
)

/**
 * The rail: what fans shouted at this game, and what the players said on it.
 *
 * The fan half is GAME-scoped (`GameFeedComposer`), not team-scoped. Reusing the
 * club feed put season-long lines in a live game's composer and, worse, showed a
 * post made at one game in every other game's rail.
 */
const GameBleachers: React.FC<{
  entries: RailEntry[]
  watching: number | null
  gameId: number
  /** Speaker colour, by team id. The page holds these — and holds the away team's
   *  DISPLAY colour, which is its secondary when the two primaries are too close to
   *  tell apart, so the rail inherits that fix rather than repeating the comparison. */
  teamColors?: Record<number, string>
  /** The two cheer buttons. Inside the panel rather than floating above it —
   *  rallying IS being in the stands, and as its own card it read as a
   *  separate feature that happened to sit nearby. */
  rally?: React.ReactNode
}> = ({ entries, watching, gameId, rally, teamColors }) => (
  <div style={{
    background: BG.card, border: `1px solid ${BORDER.hairline}`,
    display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
  }}>
    <div style={{
      padding: '12px 16px', background: BG.panel,
      borderBottom: `1px solid ${BORDER.raised}`,
      display: 'flex', alignItems: 'center', gap: '11px', flexShrink: 0,
    }}>
      <span style={{ ...font(800, 12, 1, '0.02em'), color: TEXT.strong }}>THE BLEACHERS</span>
      <span style={{ flex: 1 }} />
      {watching != null && watching > 0 && (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <LiveDot />
          <span style={{ ...font(600, 11), color: ACCENT.live, ...TABULAR }}>{watching} watching</span>
        </span>
      )}
    </div>

    {rally && (
      <div style={{
        padding: '12px 14px', borderBottom: `1px solid ${BORDER.hairline}`,
        display: 'flex', gap: '10px', flexShrink: 0,
      }}>{rally}</div>
    )}

    {/* The fan half: shouts at THIS game, from either stand. The player and
        sideline voices ride in the same scroller. */}
    {/* ⚠️ Handed over as DATA, not as a rendered block. The composer is the only place
        that can see both halves, so it is the only place that can put them in the order
        they were said — appending a block of sideline lines after the posts is what made
        every player line sit under every shout. */}
    <GameFeedComposer
      gameId={gameId}
      hasExtraEntries={entries.length > 0}
      timelineEntries={entries.map((entry): TimelineEntry => ({
        key: entry.key,
        createdAt: entry.createdAt,
        node: <Entry entry={entry} teamColor={entry.teamId != null ? teamColors?.[entry.teamId] : undefined} />,
      }))}
    />
  </div>
)

export default GameBleachers

/** Merge is memoised at the call site; this is only the shaping. */
export function useRailEntries(plays: any[] | undefined): RailEntry[] {
  return useMemo(() => railEntriesFromPlays(plays), [plays])
}
